<template>
  <div class="flex gap-3">
    <button
      v-for="(item, i) in items"
      :key="i"
      type="button"
      class="p-0 m-0"
      @click="openInNewTab(item?.link)"
    >
      <component
        :is="item.icon"
        v-if="item?.link"
        class="w-5 h-5 transition hover:scale-105"
      />
    </button>
    <button class="p-0 m-0" @click="handleShare">
      <IconLink45Deg class="w-5 h-5" />
    </button>
  </div>
</template>

<script setup lang="ts">
import type { ToastOptions } from 'vue3-toastify'
import { toast } from '~/utils/lazyToast'
import IconFacebook from '~/components/icons/IconFacebook.vue'
import { IconLinkedIn } from '#components'
import IconPinterest from '~/components/icons/IconPinterest.vue'
import IconLink45Deg from '~/components/icons/IconLink45Deg.vue'

interface Props {
  image?: string
  title?: string
  description?: string
  seeMoreSrc?: string
  titleBelowImage?: boolean
}

const props = defineProps<Props>()

const fullLocalizedUrl = useFullLocalizedUrl(true)
const $route = useRoute()
const { t } = useI18n()

const items = computed(() => [
  {
    icon: IconFacebook,
    link: `https://www.facebook.com/sharer.php?u=${fullLocalizedUrl(
      $route.fullPath
    )}`,
  },
  {
    icon: IconLinkedIn,
    link: `https://linkedin.com/shareArticle?url=${fullLocalizedUrl(
      $route.fullPath
    )}`,
  },
  {
    icon: IconPinterest,
    link: `https://pinterest.com/pin/create/button/?url=${fullLocalizedUrl(
      $route.fullPath
    )}&media=${props.image}&description=${props.description
      ?.replace(/<[^>]*>?/g, '')
      ?.slice(0, 100)}`,
  },
])

const openInNewTab = (url: string) => {
  window.open(url, '_blank', 'noopener,noreferrer')
}

const handleShare = () => {
  if (!navigator.share) {
    console.warn("Your browser doesn't support the Web Share API.")
    copyToClipboard(fullLocalizedUrl($route.fullPath))
    return
  }

  const data = {
    url: fullLocalizedUrl($route.fullPath),
    title: t('general.titles.base'),
  }

  navigator.share(data).catch((error) => {
    console.error(`Error: ${error.message}`)
    copyToClipboard(fullLocalizedUrl($route.fullPath))
  })
}

// copy to clipboard
const copyToClipboard = (link: string) => {
  const textArea = document.createElement('textarea')
  textArea.value = `${link}?utm_source=konfetti&utm_medium=share_event&utm_campaign=copy_link`
  document.body.appendChild(textArea)
  textArea.focus({ preventScroll: true })
  textArea.select()

  try {
    document.execCommand('copy')
    toast(t('general.copiedToClipboard'), {
      autoClose: 2000,
      type: 'success',
      position: toast.POSITION.TOP_CENTER,
      multiple: false,
    } as ToastOptions)
  } catch (err) {
    console.error('Unable to copy to clipboard', err)
  }

  textArea.remove()
}
</script>
